import React, { useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader, Button, Row, Col, FormGroup, Label, Input, Alert, FormFeedback } from 'reactstrap';
import { toast } from 'react-toastify';
import { handleAddModifyOperation } from '../../../../Api/ApiMethods';
import { shouldToastError } from '../../../../Api/utils';
import { useModalCrudState } from '../../../../_helper/hooks/useModalCrudState';
import { FILE_CLOSE_DESCRIPTIONS } from '../../../../_helper/schemaAllowedValues';

const FileCloseModal = ({ isOpen, toggle, arbitrationId, refreshArbitrationData }) => {
    const initialFormData = {
        willReapply: '',
        fileCloseDescription: '',
        fileCloseComment: '',
        fileCloseDescriptionOther: ''
    };

    const {
        formData,
        setFormData,
        handleCancel,
    } = useModalCrudState({
        isOpen,
        mode: 'add',
        selectedItem: null,
        initialData: initialFormData,
        toggle,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setErrors(prev => ({ ...prev, [name]: '' }));

        if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [name]: checked }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Tekrar başvuru bilgisini buton seçimine göre günceller
    const handleRadioChange = (value) => {
        setErrors(prev => ({ ...prev, willReapply: '' }));
        setFormData(prev => ({
            ...prev,
            willReapply: value
        }));
    };

    const handleModalCancel = () => {
        setErrors({});
        handleCancel();
    };

    // Dosya kapatma verisini doğrulayıp API'ye gönderir
    const handleSubmit = async (e) => {
        e.preventDefault();

        const nextErrors = {};

        if (formData.willReapply !== 'true' && formData.willReapply !== 'false') {
            nextErrors.willReapply = 'Tekrar başvuru yapılacak mı seçimi zorunludur.';
        }

        if (!formData.fileCloseDescription.trim()) {
            nextErrors.fileCloseDescription = 'Dosya kapatma açıklaması zorunludur.';
        }

        if (!formData.fileCloseComment.trim()) {
            nextErrors.fileCloseComment = 'Ek yorum zorunludur.';
        }

        if (formData.fileCloseDescription === 'Diğer' && !formData.fileCloseDescriptionOther.trim()) {
            nextErrors.fileCloseDescriptionOther = 'Diğer dosya kapatma açıklaması zorunludur.';
        }

        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors);
            toast.error('Lütfen zorunlu dosya kapatma bilgilerini doldurun.');
            return;
        }

        setIsSubmitting(true);

        try {
            const apiData = {
                reapplicationInformation: {
                    arbitration: [arbitrationId],
                    willReapply: formData.willReapply === 'true',
                    willWriteExpertReport: false,
                    fileCloseDescription: formData.fileCloseDescription,
                    fileCloseDescriptionOther: formData.fileCloseDescription === 'Diğer' ? formData.fileCloseDescriptionOther : '',
                    comments: formData.fileCloseComment ? [{
                        commentType: "Genel",
                        comment: formData.fileCloseComment
                    }] : []
                },
                accidentFileCloseInformation: {
                    fileStatus: "Kapalı",
                    fileCloseDescription: formData.fileCloseDescription,
                    fileCloseDescriptionOther: formData.fileCloseDescription === 'Diğer' ? formData.fileCloseDescriptionOther : '',
                    fileCloseComment: formData.fileCloseComment
                }
            };

            const fileCloseApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/Arbitration/patch-arbitration-close';

            await handleAddModifyOperation({
                apiEndpoint: fileCloseApiUrl,
                selectedItem: { id: arbitrationId },
                mode: 'modify',
                data: apiData,
                httpMethod: 'PUT',
                refreshDataCallback: () => {
                    refreshArbitrationData();
                    handleCancel();
                },
                componentName: 'Dosya kapatma'
            });

        } catch (error) {
            if (shouldToastError(error)) {
                toast.error('Dosya kapatma işlemi sırasında hata oluştu. Lütfen tekrar deneyin.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={handleModalCancel} size="lg">
            <ModalHeader toggle={handleModalCancel}>
                Dosya Kapatma
            </ModalHeader>
            <ModalBody>
                <Alert color="warning" className="mb-4">
                    <i className="fa fa-warning me-2"></i>
                    Bu işlem tahkim dosyasını kapatacaktır. Lütfen gerekli bilgileri doldurun.
                </Alert>

                <form onSubmit={handleSubmit}>
                    <Row>
                        <Col md="12">
                            <FormGroup className="mb-3">
                                <Label className="text-nowrap mb-2">Tekrar Başvuru Yapılacak Mı? <span className="text-danger">*</span></Label>
                                <div className="d-flex mt-2">
                                    <Button
                                        type="button"
                                        color={formData.willReapply === 'true' ? 'success' : 'outline-success'}
                                        className="me-3"
                                        onClick={() => {
                                            handleRadioChange('true');
                                        }}
                                    >
                                        Evet {formData.willReapply === 'true' && '✓'}
                                    </Button>
                                    <Button
                                        type="button"
                                        color={formData.willReapply === 'false' ? 'danger' : 'outline-danger'}
                                        onClick={() => {
                                            handleRadioChange('false');
                                        }}
                                    >
                                        Hayır {formData.willReapply === 'false' && '✓'}
                                    </Button>
                                </div>
                                {errors.willReapply && <div className="text-danger small mt-1">{errors.willReapply}</div>}
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="12">
                            <FormGroup className="mb-4">
                                <Label for="fileCloseDescription">Dosya Kapatma Yorumu <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    name="fileCloseDescription"
                                    id="fileCloseDescription"
                                    value={formData.fileCloseDescription}
                                    onChange={handleInputChange}
                                    invalid={!!errors.fileCloseDescription}
                                >
                                    <option value="">Dosya kapama sebebini seçin...</option>
                                    {FILE_CLOSE_DESCRIPTIONS.map(description => (
                                        <option key={description} value={description}>{description}</option>
                                    ))}
                                </Input>
                                <FormFeedback>{errors.fileCloseDescription}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    {formData.fileCloseDescription === 'Diğer' && (
                        <Row>
                            <Col md="12">
                                <FormGroup className="mb-4">
                                    <Label for="fileCloseDescriptionOther">Manuel Dosya Kapatma Yorumu <span className="text-danger">*</span></Label>
                                    <Input
                                        type="textarea"
                                        name="fileCloseDescriptionOther"
                                        id="fileCloseDescriptionOther"
                                        value={formData.fileCloseDescriptionOther}
                                        onChange={handleInputChange}
                                        placeholder="Manuel dosya kapatma yorumunuzu buraya yazın..."
                                        rows="3"
                                        invalid={!!errors.fileCloseDescriptionOther}
                                    />
                                    <FormFeedback>{errors.fileCloseDescriptionOther}</FormFeedback>
                                </FormGroup>
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Col md="12">
                            <FormGroup className="mb-4">
                                <Label for="fileCloseComment">Ek Yorum <span className="text-danger">*</span></Label>
                                <Input
                                    type="textarea"
                                    name="fileCloseComment"
                                    id="fileCloseComment"
                                    value={formData.fileCloseComment}
                                    onChange={handleInputChange}
                                    placeholder="Dosya kapatma ile ilgili ek yorumlarınızı buraya yazın..."
                                    rows="3"
                                    invalid={!!errors.fileCloseComment}
                                />
                                <FormFeedback>{errors.fileCloseComment}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>
                </form>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={handleModalCancel} disabled={isSubmitting}>
                    İptal
                </Button>
                <Button
                    color="danger"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Dosya Kapatılıyor...' : 'Dosyayı Kapat'}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default FileCloseModal;
